import { createTheme, ThemeOptions } from '@mui/material/styles';
import { darkTokens, lightTokens } from './tokens';

declare module '@mui/material/styles' {
  interface Palette {
    brand: {
      navy: string;
      yellow: string;
      accent: string;
      accentContrast: string;
    };
    border: string;
    raised: string;
    mutedText: string;
    danger: Palette['error'];
    alert: {
      critical: { bg: string; text: string; border: string };
      attention: { bg: string; text: string; border: string };
      caution: { bg: string; text: string; border: string };
      info: { bg: string; text: string; border: string };
      success: { bg: string; text: string; border: string };
    };
  }
  interface PaletteOptions {
    brand?: {
      navy?: string;
      yellow?: string;
      accent?: string;
      accentContrast?: string;
    };
    border?: string;
    raised?: string;
    mutedText?: string;
    danger?: PaletteOptions['error'];
    alert?: Palette['alert'];
  }
}

export type ColorwayId = 'default' | 'emerald' | 'midnight' | 'ocean' | 'sunset';

export const COLORWAY_STORAGE_KEY = 'dealskout.colorway';

export const COLORWAYS: Array<{
  id: ColorwayId;
  label: string;
  navy: string;
  accent: string;
  accentContrast: string;
  page: string;
  pageDark: string;
  paperDark: string;
}> = [
  { id: 'default', label: 'DealSkout', navy: '#0B1F3A', accent: '#F5C518', accentContrast: '#0B1F3A', page: '#F4F6F9', pageDark: '#071525', paperDark: '#0F2744' },
  { id: 'emerald', label: 'Emerald Growth', navy: '#0C3B2E', accent: '#8ED9A8', accentContrast: '#06281E', page: '#F3F7F4', pageDark: '#071A14', paperDark: '#0E2E24' },
  { id: 'midnight', label: 'Midnight Gold', navy: '#14110C', accent: '#E2B657', accentContrast: '#1A1408', page: '#F7F4EE', pageDark: '#100E0A', paperDark: '#241E14' },
  { id: 'ocean', label: 'Ocean Blue', navy: '#083044', accent: '#8ECAE6', accentContrast: '#06283D', page: '#F2F7FA', pageDark: '#061820', paperDark: '#0C2C3D' },
  { id: 'sunset', label: 'Sunset Copper', navy: '#3A1E14', accent: '#F0B27A', accentContrast: '#2A120C', page: '#FBF6F2', pageDark: '#1A0E0A', paperDark: '#3A2218' },
];

export function colorwayById(id: string | null | undefined) {
  return COLORWAYS.find((item) => item.id === id) || COLORWAYS[0];
}

/** One type scale for every mode and colorway. Only palette colors change. */
export const FONT_FAMILY = '"Source Sans 3", "Segoe UI", "Helvetica Neue", Arial, sans-serif';

const face = (
  fontWeight: number,
  fontSize: string,
  lineHeight: number,
  letterSpacing: string
) => ({
  fontFamily: FONT_FAMILY,
  fontWeight,
  fontSize,
  lineHeight,
  letterSpacing,
});

export function createDealSkoutTheme(mode: 'light' | 'dark', colorwayId: ColorwayId = 'default') {
  const base = mode === 'dark' ? darkTokens : lightTokens;
  const colorway = colorwayById(colorwayId);
  const t = {
    ...base,
    background: {
      ...base.background,
      default: mode === 'dark' ? colorway.pageDark : colorway.page,
      paper: mode === 'dark' ? colorway.paperDark : '#FFFFFF',
    },
    brand: {
      ...base.brand,
      navy: colorway.navy,
      yellow: colorway.accent,
    },
    text: {
      ...base.text,
      primary: mode === 'dark' ? base.text.primary : colorway.navy,
    },
    accent: colorway.accent,
    accentContrast: colorway.accentContrast,
  };

  const options: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: t.brand.navy,
        dark: mode === 'dark' ? t.brand.navySoft : t.brand.navy,
        light: t.brand.navySoft,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: t.brand.yellowDark,
        contrastText: t.brand.navy,
      },
      background: {
        default: t.background.default,
        paper: t.background.paper,
      },
      text: {
        primary: t.text.primary,
        secondary: t.text.secondary,
      },
      divider: t.border,
      success: t.success,
      warning: t.warning,
      error: t.danger,
      info: t.info,
      brand: {
        navy: t.brand.navy,
        yellow: t.brand.yellow,
        accent: t.accent,
        accentContrast: t.accentContrast,
      },
      border: t.border,
      raised: t.background.raised,
      mutedText: t.text.muted,
      danger: t.danger,
      alert: t.alert,
    },
    typography: {
      fontFamily: FONT_FAMILY,
      fontWeightLight: 400,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      h1: face(400, '6rem', 1.167, '-0.01562em'),
      h2: face(400, '3.75rem', 1.2, '-0.00833em'),
      h3: face(800, '3rem', 1.15, '-0.02em'),
      h4: face(800, '2.125rem', 1.2, '-0.02em'),
      h5: face(400, '1.5rem', 1.334, '0em'),
      h6: face(700, '1.25rem', 1.3, '0.0075em'),
      subtitle1: face(400, '1rem', 1.75, '0.00938em'),
      subtitle2: face(600, '0.875rem', 1.57, '0.00714em'),
      body1: face(400, '1.0625rem', 1.6, '0.00938em'),
      body2: face(400, '0.875rem', 1.55, '0.01071em'),
      button: { ...face(600, '0.875rem', 1.75, '0em'), textTransform: 'none' as const },
      caption: face(400, '0.75rem', 1.66, '0.03333em'),
      overline: face(400, '0.75rem', 2.66, '0.08333em'),
    },
    shape: { borderRadius: 8 },
    transitions: {
      duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 220,
        complex: 300,
        enteringScreen: 200,
        leavingScreen: 160,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
            },
          },
          html: {
            fontFamily: FONT_FAMILY,
            fontSynthesis: 'none',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          body: {
            backgroundColor: t.background.default,
            color: t.text.primary,
            fontFamily: FONT_FAMILY,
            fontSynthesis: 'none',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          },
          'h1, h2, h3, h4, h5, h6, p, button, input, select, textarea': {
            fontFamily: FONT_FAMILY,
            fontSynthesis: 'none',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            borderRadius: 8,
            minHeight: 44,
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
          },
          containedPrimary: {
            backgroundColor: t.brand.navy,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: t.brand.navyMid,
              boxShadow: `inset 0 -3px 0 ${t.brand.yellow}`,
            },
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? '#1A3354' : '#D5DCE6',
              color: t.text.muted,
            },
          },
          outlinedPrimary: {
            borderColor: mode === 'dark' ? t.text.secondary : t.brand.navy,
            color: mode === 'dark' ? t.text.primary : t.brand.navy,
            '&:hover': {
              borderWidth: 2,
              borderColor: mode === 'dark' ? t.text.primary : t.brand.navy,
              backgroundColor: mode === 'dark' ? 'rgba(242,245,249,0.08)' : 'rgba(11,31,58,0.06)',
            },
            '&.Mui-disabled': {
              color: t.text.muted,
              borderColor: t.border,
            },
          },
          text: {
            color: t.text.primary,
            '&:hover': {
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
          textPrimary: {
            color: mode === 'dark' ? t.text.primary : t.brand.navy,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: t.text.primary,
            fontFamily: FONT_FAMILY,
            minWidth: 44,
            minHeight: 44,
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
          colorPrimary: {
            color: mode === 'dark' ? t.text.primary : t.brand.navy,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: t.text.primary,
            borderColor: t.border,
            minHeight: 44,
            minWidth: 44,
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
            '&.Mui-selected': {
              color: t.brand.navy,
              backgroundColor: t.brand.yellow,
              fontWeight: 600,
              '&:hover': { backgroundColor: t.brand.yellow },
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: t.background.paper,
            color: t.text.primary,
            borderBottom: `1px solid ${t.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            color: t.text.secondary,
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(245,197,24,0.12)' : 'rgba(11,31,58,0.06)',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            },
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
            '&.Mui-selected': {
              color: t.text.primary,
              fontWeight: 600,
              backgroundColor: mode === 'dark' ? 'rgba(245,197,24,0.16)' : 'rgba(245,197,24,0.35)',
              boxShadow: `inset 0 -3px 0 ${t.brand.yellow}`,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: t.brand.yellow,
            height: 3,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: t.text.primary,
            fontWeight: 700,
            textDecorationColor: t.brand.yellow,
            textUnderlineOffset: '2px',
            '&:hover': { color: t.text.primary },
            '&:focus-visible': {
              outline: `2px solid ${t.brand.yellow}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ ownerState }: { ownerState?: { color?: string } }) =>
            mode === 'dark' && ownerState?.color === 'primary' ? { color: t.text.primary } : {},
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: t.text.secondary,
            '&.Mui-focused': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { color: t.text.primary, fontFamily: FONT_FAMILY },
          input: {
            '&::placeholder, &::-webkit-input-placeholder': {
              color: t.text.secondary,
              opacity: 1,
            },
            '&:focus::placeholder, &:focus::-webkit-input-placeholder': {
              color: t.text.secondary,
              opacity: 1,
            },
            '&.Mui-disabled': {
              color: t.text.muted,
              WebkitTextFillColor: t.text.muted,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.text.secondary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: t.text.secondary,
            '&.Mui-checked': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: t.text.secondary,
            '&.Mui-checked': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: mode === 'dark' ? t.brand.yellow : t.brand.navy,
            },
            '&.Mui-disabled': { color: t.text.muted },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardError: {
            backgroundColor: t.alert.critical.bg,
            color: t.alert.critical.text,
            border: `1px solid ${t.alert.critical.border}`,
            '& .MuiAlert-icon': { color: t.alert.critical.border },
          },
          standardWarning: {
            backgroundColor: t.alert.attention.bg,
            color: t.alert.attention.text,
            border: `1px solid ${t.alert.attention.border}`,
            '& .MuiAlert-icon': { color: t.alert.attention.border },
          },
          standardInfo: {
            backgroundColor: t.alert.info.bg,
            color: t.alert.info.text,
            border: `1px solid ${t.alert.info.border}`,
            '& .MuiAlert-icon': { color: t.alert.info.border },
          },
          standardSuccess: {
            backgroundColor: t.alert.success.bg,
            color: t.alert.success.text,
            border: `1px solid ${t.alert.success.border}`,
            '& .MuiAlert-icon': { color: t.alert.success.border },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 700 },
          outlined: {
            borderColor: t.border,
            color: t.text.primary,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: t.background.raised,
            color: t.text.primary,
            border: `1px solid ${t.border}`,
            fontSize: '0.8125rem',
          },
        },
      },
    },
  };

  return createTheme(options);
}
